import { useEffect, useState } from "react";
import { Facebook, Clock, Sparkles, Play, CheckCheck, Globe } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, StatusBadge, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";

const StatusPanel = ({ s, admin, onPost, onRun, busy }) => (
  <Panel title="État de l'agent">
    <div className="grid gap-4 sm:grid-cols-3 text-sm">
      <div data-testid="agent-fb-status"><p className="text-xs uppercase tracking-wider text-slate-500">Facebook</p>
        <p className={`mt-1 flex items-center gap-1 font-semibold ${s.facebook_connected ? "text-emerald-600" : "text-amber-600"}`}><Facebook className="h-4 w-4" /> {s.facebook_connected ? "Connecté" : "Non connecté (accès Meta à fournir)"}</p></div>
      <div><p className="text-xs uppercase tracking-wider text-slate-500">Réponses groupées</p><p className="mt-1 flex items-center gap-1 font-semibold"><Clock className="h-4 w-4" /> {s.reply_times.join(" · ")}</p></div>
      <div><p className="text-xs uppercase tracking-wider text-slate-500">Publication IA</p><p className="mt-1 font-semibold">Toutes les {s.post_every_hours} h</p></div>
      <div><p className="text-xs uppercase tracking-wider text-slate-500">Messages en attente</p><p className="mt-1 font-display text-2xl font-bold" data-testid="agent-pending">{s.pending}</p></div>
      <div><p className="text-xs uppercase tracking-wider text-slate-500">Questions à traiter</p><p className="mt-1 font-display text-2xl font-bold text-red-600" data-testid="agent-escalated">{s.escalated}</p></div>
      <div className="text-xs text-slate-500">Webhook Meta : <span className="break-all font-mono">{s.webhook_url}</span></div>
    </div>
    {admin && (
      <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        <Button className="bg-red-600 hover:bg-red-700" onClick={onPost} disabled={busy} data-testid="agent-generate-post-btn"><Sparkles className="mr-1 h-4 w-4" /> {busy ? "Rédaction…" : "Générer une publication IA maintenant"}</Button>
        <Button variant="outline" onClick={onRun} disabled={!s.facebook_connected} data-testid="agent-run-replies-btn"><Play className="mr-1 h-4 w-4" /> Répondre maintenant sur Facebook</Button>
      </div>
    )}
  </Panel>
);

const Conversation = ({ c, onResolve }) => (
  <div className={`rounded-md border bg-white p-4 ${c.escalated ? "border-red-300" : "border-slate-200"}`} data-testid={`conversation-${c.id}`}>
    <div className="mb-2 flex items-center justify-between text-xs">
      <span className="flex items-center gap-1 font-semibold uppercase tracking-wider text-slate-500">{c.channel === "facebook" ? <Facebook className="h-3 w-3" /> : <Globe className="h-3 w-3" />} {c.channel === "facebook" ? "Facebook" : "Chat du site"} · {new Date(c.updated_at).toLocaleString("fr-FR")}</span>
      {c.escalated && <button onClick={() => onResolve(c)} className="flex items-center gap-1 font-semibold text-red-600 hover:underline" data-testid={`conversation-resolve-${c.id}`}><CheckCheck className="h-3 w-3" /> Marquer traitée</button>}
    </div>
    <div className="max-h-56 space-y-1.5 overflow-y-auto text-sm">
      {c.messages.slice(-8).map((m, i) => <p key={i} className={m.role === "user" ? "text-slate-900" : "border-l-2 border-red-500 pl-2 text-slate-600"}><b>{m.role === "user" ? "Client" : "Agent"} :</b> {m.text}</p>)}
    </div>
  </div>
);

export default function Agent() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [convs, setConvs] = useState([]);
  const [posts, setPosts] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => {
    api.get("/agent/status").then((r) => setS(r.data));
    api.get("/agent/conversations").then((r) => setConvs(r.data));
    api.get("/agent/posts").then((r) => setPosts(r.data));
  };
  useEffect(() => { load(); }, []);

  const onPost = async () => {
    setBusy(true);
    try { const r = await api.post("/agent/posts/generate"); toast.success(r.data.status === "publie" ? "Publication publiée sur Facebook" : "Publication rédigée (Facebook non connecté)"); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const onRun = async () => { try { await api.post("/agent/run-replies"); toast.success("Traitement lancé"); } catch (e) { toast.error(errMsg(e)); } };
  const onResolve = async (c) => { await api.post(`/agent/conversations/${c.id}/resolve`); load(); };

  if (!s) return <p className="text-sm text-slate-500">Chargement…</p>;
  return (
    <div>
      <PageHeader kicker="Agent IA" title="Assistant Facebook & chat du site" />
      <StatusPanel s={s} admin={user.role === "admin"} onPost={onPost} onRun={onRun} busy={busy} />
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section>
          <h2 className="mb-3 font-display text-lg font-semibold">Conversations</h2>
          <div className="space-y-3">{convs.length ? convs.map((c) => <Conversation key={c.id} c={c} onResolve={onResolve} />) : <Empty text="Aucune conversation." />}</div>
        </section>
        <section>
          <h2 className="mb-3 font-display text-lg font-semibold">Publications IA</h2>
          <div className="space-y-3">
            {posts.length ? posts.map((p) => (
              <div key={p.id} className="rounded-md border border-slate-200 bg-white p-4" data-testid={`post-${p.id}`}>
                <div className="mb-2 flex items-center justify-between text-xs"><span className="font-semibold text-slate-500">{p.topic} · {new Date(p.created_at).toLocaleString("fr-FR")}</span><StatusBadge status={p.status} /></div>
                <p className="whitespace-pre-line text-sm text-slate-700">{p.text}</p>
                {p.error && <p className="mt-2 text-xs text-red-600">{p.error}</p>}
              </div>
            )) : <Empty text="Aucune publication pour le moment." />}
          </div>
        </section>
      </div>
    </div>
  );
}
